import { useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, ShieldCheck, AlertTriangle, CalendarClock, HelpCircle, Archive, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import API, { apiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ErpHeading, ErpStatus, ErpPagination, useErpList } from '../../components/erp/ErpShared';
import { RequirementForm, ComplianceRecordForm } from '../../components/erp/ComplianceForms';
import '../../components/erp/assets.css';

const STATUS = {
  compliant: { label: 'Conform', dot: 'ok', cls: 'text-emerald-400 bg-emerald-500/10' },
  expiring: { label: 'Expiră curând', dot: 'expiring', cls: 'text-amber-400 bg-amber-500/10' },
  expired: { label: 'Expirat', dot: 'expired', cls: 'text-rose-400 bg-rose-500/10' },
  missing: { label: 'Fără termen', dot: 'none', cls: 'text-zinc-400 bg-zinc-500/10' },
  not_applicable: { label: 'Nu se aplică', dot: 'none', cls: 'text-slate-400 bg-slate-500/10' },
};
const KIND_LABEL = { vehicle: 'Vehicul', equipment: 'Utilaj', employee: 'Angajat' };
const fmt = s => s ? new Date(s).toLocaleDateString('ro-RO') : '—';

export default function CompliancePage() {
  const { permissions } = useOutletContext();
  const canWrite = permissions.includes('compliance.write');
  const [stats, setStats] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [targets, setTargets] = useState({});
  const [status, setStatus] = useState('');
  const [v, setV] = useState(0);
  const [recForm, setRecForm] = useState(null);
  const [reqForm, setReqForm] = useState(null);

  const recQuery = useMemo(() => `${status ? `&status=${status}` : ''}&_r=${v}`, [status, v]);
  const recList = useErpList('/erp/compliance/records', recQuery);
  const reqList = useErpList('/erp/compliance/requirements', `&_r=${v}`);
  const loadAux = useCallback(() => {
    API.get('/erp/compliance/stats').then(r => setStats(r.data)).catch(() => {});
    API.get('/erp/compliance/requirements?page_size=200').then(r => setRequirements(r.data.items)).catch(() => {});
    API.get('/erp/compliance/targets').then(r => setTargets(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAux(); }, [loadAux, v]);
  const refresh = () => setV(x => x + 1);
  const archive = async (kind, id) => { const reason = window.prompt('Motivul arhivării?'); if (!reason) return;
    try { await API.delete(`/erp/compliance/${kind}/${id}`, { data: { reason } }); toast.success('Arhivat'); refresh(); } catch (e) { toast.error(apiError(e)); } };

  const kpis = stats ? [
    { k: 'total', v: stats.total, l: 'Înregistrări', c: '#3b82f6', icon: ShieldCheck },
    { k: 'expired', v: stats.expired, l: 'Expirate', c: '#f43f5e', icon: AlertTriangle },
    { k: 'expiring', v: stats.expiring, l: 'Expiră în 30 zile', c: '#f59e0b', icon: CalendarClock },
    { k: 'missing', v: stats.missing, l: 'Fără termen', c: '#a1a1aa', icon: HelpCircle },
  ] : [];
  const noReq = requirements.length === 0;

  return <>
    <ErpHeading title="Conformitate ISCIR / RSVTI" count={stats ? `${stats.requirements} cerințe · ${stats.total} înregistrări` : undefined} />

    <div className="asset-kpis" data-testid="compliance-kpis">{kpis.map(k => <div className="asset-kpi" key={k.k} style={{ '--kpi-accent': k.c }} data-testid={`ckpi-${k.k}`}>
      <p className="kpi-val" style={{ color: k.c }}>{k.v}</p><p className="kpi-lbl"><k.icon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{k.l}</p></div>)}</div>

    <Tabs defaultValue="records">
      <TabsList><TabsTrigger value="records" data-testid="ctab-records">Panou conformitate</TabsTrigger><TabsTrigger value="requirements" data-testid="ctab-requirements">Cerințe configurate</TabsTrigger></TabsList>

      <TabsContent value="records" className="pt-5">
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <select value={status} onChange={e => setStatus(e.target.value)} className="bg-[#18181b] border border-[#3f3f46] rounded-md px-3 h-10 text-sm" data-testid="compliance-status-filter">
            <option value="">Toate stările</option>{Object.entries(STATUS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</select>
          <div className="flex-1" />
          {canWrite && <Button onClick={() => setRecForm({})} disabled={noReq} data-testid="compliance-add-record"><Plus className="w-4 h-4 mr-2" />Înregistrare conformitate</Button>}
        </div>
        {canWrite && noReq && <p className="text-sm text-amber-300/90 mb-3" data-testid="compliance-no-req">Definește întâi o cerință în tabul „Cerințe configurate".</p>}
        <ErpStatus list={recList} />
        {!recList.loading && !recList.error && <div className="space-y-2" data-testid="compliance-records">{recList.items.map(r => {
          const s = STATUS[r.status] || STATUS.missing;
          return <div key={r.id} className="erp-record !border rounded-xl px-4" style={{ borderColor: 'hsl(var(--border))' }} data-testid={`crec-${r.id}`}>
            <div className="flex-1 min-w-0"><div className="flex items-center gap-2 flex-wrap"><span className={`dot ${s.dot}`} /><p className="font-medium truncate">{r.requirement_name}</p>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span></div>
              <p className="text-xs text-muted-foreground mt-1">{KIND_LABEL[r.target_kind]}: {r.target_name} · termen {fmt(r.next_due)}{r.responsible ? ` · ${r.responsible}` : ''}</p></div>
            {canWrite && <div className="flex gap-1 shrink-0"><Button size="icon" variant="ghost" onClick={() => setRecForm(r)} data-testid={`crec-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => archive('records', r.id)}><Archive className="w-4 h-4 text-rose-400" /></Button></div>}
          </div>;
        })}</div>}
        {!recList.loading && !recList.error && recList.items.length > 0 && <ErpPagination list={recList} />}
      </TabsContent>

      <TabsContent value="requirements" className="pt-5">
        <div className="flex justify-end mb-4">{canWrite && <Button onClick={() => setReqForm({})} data-testid="compliance-add-req"><Plus className="w-4 h-4 mr-2" />Cerință nouă</Button>}</div>
        <ErpStatus list={reqList} />
        {!reqList.loading && !reqList.error && <div className="space-y-2" data-testid="compliance-requirements">{reqList.items.map(r => <div key={r.id} className="erp-record !border rounded-xl px-4" style={{ borderColor: 'hsl(var(--border))' }} data-testid={`creq-${r.id}`}>
          <div className="flex-1 min-w-0"><div className="flex items-center gap-2 flex-wrap"><p className="font-medium truncate">{r.name}</p>{r.authority && <span className="text-[11px] px-2 py-0.5 rounded bg-blue-500/10 text-blue-300">{r.authority}</span>}</div>
            <p className="text-xs text-muted-foreground mt-1">Aplică: {({ any: 'orice', vehicle: 'vehicule', equipment: 'utilaje', employee: 'angajați' })[r.applies_to]}{r.interval_months ? ` · la fiecare ${r.interval_months} luni` : ''}{r.reference ? ` · ${r.reference}` : ''}</p></div>
          {canWrite && <div className="flex gap-1 shrink-0"><Button size="icon" variant="ghost" onClick={() => setReqForm(r)} data-testid={`creq-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => archive('requirements', r.id)}><Archive className="w-4 h-4 text-rose-400" /></Button></div>}
        </div>)}</div>}
        {!reqList.loading && !reqList.error && reqList.items.length > 0 && <ErpPagination list={reqList} />}
      </TabsContent>
    </Tabs>

    {recForm && <ComplianceRecordForm record={recForm.id ? recForm : null} requirements={requirements} targets={targets} onClose={() => setRecForm(null)} onSaved={refresh} />}
    {reqForm && <RequirementForm record={reqForm.id ? reqForm : null} onClose={() => setReqForm(null)} onSaved={refresh} />}
  </>;
}
